import ApplicationController, { observe, raw, unobserve } from '@aha-app/mvc';
import DataStoreWidgets, {
  type WidgetTypes,
} from '../collaboration/DataStoreWidgets';
import VotingBehaviors from '../objects/VotingBehaviors';
import { type SerializedWidget, type WidgetType } from '../widgets';
import type BaseWidget from '../widgets/BaseWidget';
import TimerWidget from '../widgets/TimerWidget';
import VotingSessionWidget from '../widgets/VotingSessionWidget';
import widgetFromJSON from '../widgets/widgetFromJSON';
import WhiteboardController from './WhiteboardController';

interface WidgetsControllerState {
  votingSessionWidgets: VotingSessionWidget[];
  timerWidget: TimerWidget | null;
  // The voting session widget that has been selected by the
  // user. Used primarily to display historical voting sessions.
  selectedVotingSessionWidget: VotingSessionWidget | null;
}

class WidgetsController extends ApplicationController<WidgetsControllerState> {
  whiteboardController: WhiteboardController;
  dataStore: DataStoreWidgets;

  // Whichever widget should power the display of the voting controls.
  votingSessionWidget?: VotingSessionWidget;
  votingWidgetObserver: ReturnType<typeof observe>;

  startController() {
    this.whiteboardController =
      this.findControllerInstance(WhiteboardController)!;
    this.whiteboardController.widgetsController = this;

    this.dataStore = new DataStoreWidgets(
      this,
      this.whiteboardController.client
    );
    this.whiteboardController.dataStores.widgets = this.dataStore;

    this.votingWidgetObserver = observe(() => {
      this.votingSessionWidget =
        this.state.selectedVotingSessionWidget ??
        this.activeVotingSessionWidget;

      if (this.votingSessionWidget) {
        VotingBehaviors.enable();
      } else {
        VotingBehaviors.disable();
      }
    });
  }

  destroy() {
    unobserve(this.votingWidgetObserver);
  }

  get initialState(): WidgetsControllerState {
    return {
      votingSessionWidgets: [],
      timerWidget: null,
      selectedVotingSessionWidget: null,
    };
  }

  get timerWidget(): TimerWidget | null {
    return this.state.timerWidget;
  }

  get votingSessionWidgets(): VotingSessionWidget[] {
    return this.state.votingSessionWidgets;
  }

  getAllWidgets(): BaseWidget[] {
    const widgets: BaseWidget[] = [...this.votingSessionWidgets];
    if (this.timerWidget) {
      widgets.push(this.timerWidget);
    }
    return widgets;
  }

  // The first voting session that is currently running (has started
  // and not yet stopped)
  get activeVotingSessionWidget(): VotingSessionWidget | undefined {
    if (!this.whiteboardController.canVote) return;

    const candidate = this.state.votingSessionWidgets.find(
      widget => widget.isActive
    );

    return candidate ?? undefined;
  }

  actionSelectVotingSessionWidget(widget: VotingSessionWidget | null) {
    this.state.selectedVotingSessionWidget = widget;
    this.whiteboardController.canvas.requestRenderAll();
  }

  addWidget(widget: WidgetTypes) {
    this.dataStore.addRecord(widget);
  }

  actionAddWidget(
    type: WidgetType,
    attrs: Partial<SerializedWidget> = {}
  ): BaseWidget {
    const authorId = this.whiteboardController.state.userId ?? undefined;
    const widget = widgetFromJSON({
      type,
      authorId,
      ...attrs,
      controller: this.whiteboardController,
    });
    this.addWidget(widget);

    return widget;
  }

  actionRemoveWidget(widget: WidgetTypes) {
    this.dataStore.removeRecord(widget);
  }

  actionUpdateWidget(widget: BaseWidget, attrs: Partial<SerializedWidget>) {
    Object.entries(attrs).forEach(([key, value]) => {
      this.whiteboardController.undoManager.withoutUndo(() => {
        widget.set(key, value);
      });
    });
  }

  restoreWidget(widget: WidgetTypes) {
    if (widget instanceof VotingSessionWidget) {
      this.state.votingSessionWidgets.push(widget);
    } else {
      this.state.timerWidget = widget;
    }
  }

  removeWidget(widget: WidgetTypes) {
    if (widget instanceof TimerWidget) {
      this.state.timerWidget = null;
    } else {
      const index = this.state.votingSessionWidgets.findIndex(
        _widget => _widget.id === widget.id
      );
      if (index >= 0) {
        // Splice triggers multiple observable reactions, some of which
        // happen while the array is in a broken intermediate state
        // (length is inconsistent with the values, for example.)
        //
        // To avoid this, we can use a scheduler to defer updates. But
        // doing the splice on the raw object and reassigning it has the
        // same effect here.
        const widgets = raw(this.state.votingSessionWidgets);
        widgets.splice(index, 1);
        this.state.votingSessionWidgets = widgets;
      }
    }
  }
}

export default WidgetsController;
